/**
 * TrialOrgSetup — Auto-provisions an organization for free trial signups.
 * Reads org metadata from auth user_metadata, creates the org + assigns the user,
 * then refreshes context so ProtectedRoute picks up the new org.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Loader2, Rocket, CheckCircle2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import type { DynamicPayload, DynamicTable } from "@/types/supabase-helpers";

interface Props {
  onComplete: () => void;
}

export default function TrialOrgSetup({ onComplete }: Props) {
  const { user } = useAuth();
  const [status, setStatus] = useState<"creating" | "done" | "error">("creating");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const provision = async () => {
      try {
        const meta = user.user_metadata || {};
        const orgName = meta.organization_name || "My Organization";
        const isAssociationTrial = meta.trial_type === "association";
        const orgSlug = orgName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "")
          .slice(0, 60);
        const orgId = `trial-${orgSlug}-${Date.now()}`;
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // 1. Create the organization (marked as trial — serves as HQ org for association trials)
        const { error: orgErr } = await supabase.from("organizations").insert({
          id: orgId,
          name: isAssociationTrial ? `${orgName} — HQ` : orgName,
          timezone,
          invite_mode: "open",
          is_trial: true,
        } as DynamicPayload);
        if (orgErr) throw orgErr;

        // 2. Assign user as org_admin
        const { error: ouErr } = await supabase.from("organization_users").insert({
          organization_id: orgId,
          user_id: user.id,
          role_type: "org_admin",
          status: "active",
          is_primary_org: true,
          display_name: meta.display_name || `${meta.first_name || ""} ${meta.last_name || ""}`.trim(),
          email: user.email,
          job_title: meta.role_title || null,
          joined_at: new Date().toISOString(),
        });
        if (ouErr) throw ouErr;

        // 3. For association trials, also create a trial association + link
        let associationId: string | null = null;
        if (isAssociationTrial) {
          associationId = `trial-assoc-${orgSlug}-${Date.now()}`;
          const { error: assocErr } = await supabase.from("associations").insert({
            id: associationId,
            name: orgName,
            created_by: user.id,
          });
          if (assocErr) {
            console.warn("Association creation failed (non-blocking):", assocErr);
          } else {
            // Link HQ org to association
            await supabase
              .from("organizations")
              .update({ association_id: associationId } as DynamicPayload)
              .eq("id", orgId);
            // Add user as association admin
            await supabase.from("association_users").insert({
              association_id: associationId,
              user_id: user.id,
              role_type: "association_admin",
              status: "active",
              joined_at: new Date().toISOString(),
            } as DynamicPayload);
          }
        }

        // 4. Create trial account record
        const { error: trialErr } = await (supabase.from as DynamicTable)("trial_accounts").insert({
          user_id: user.id,
          trial_type: isAssociationTrial ? "association" : "org",
          intended_conversion_target: isAssociationTrial ? "association" : "organization",
          provisioned_org_id: orgId,
          org_name: orgName,
          org_type: meta.org_type || meta.organization_type || null,
          member_count_estimate: meta.member_count || null,
          source: "free_trial",
          selected_plan_interest: meta.selected_plan || null,
        });
        if (trialErr) console.warn("Trial account creation failed (non-blocking):", trialErr);

        if (!cancelled) {
          setStatus("done");
          // Small delay for visual feedback then trigger reload
          setTimeout(() => {
            onComplete();
            // Force page reload to re-fetch org context
            window.location.reload();
          }, 800);
        }
      } catch (err: any) {
        console.error("Trial org setup error:", err);
        if (!cancelled) {
          setErrorMsg(err.message || "Failed to set up your organization");
          setStatus("error");
        }
      }
    };

    provision();
    return () => {
      cancelled = true;
    };
  }, [user, onComplete]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm text-center space-y-6"
      >
        {status === "creating" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center">
              <Rocket className="w-8 h-8 text-primary animate-pulse" />
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-bold text-foreground">Setting up your organization</h1>
              <p className="text-sm text-muted-foreground">This only takes a moment…</p>
            </div>
            <Loader2 className="w-6 h-6 text-primary animate-spin mx-auto" />
          </>
        )}

        {status === "done" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-success" />
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-bold text-foreground">You're all set!</h1>
              <p className="text-sm text-muted-foreground">Loading your workspace…</p>
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 mx-auto rounded-2xl bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-destructive" />
            </div>
            <div className="space-y-2">
              <h1 className="text-xl font-bold text-foreground">Something went wrong</h1>
              <p className="text-sm text-muted-foreground">{errorMsg}</p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setStatus("creating");
                setErrorMsg("");
                // Re-trigger by remounting
                window.location.reload();
              }}
            >
              Try Again
            </Button>
          </>
        )}
      </motion.div>
    </div>
  );
}
